import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { FileSignature, Loader2, Eye, Download } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { servicesEngagementAPI as API, LETTER_STATUS_META, fmtMoney, downloadLetterPdf } from '../lib/servicesEngagementAPI';

const fmt = (iso) => { if (!iso) return '—'; try { return new Date(iso).toLocaleDateString('en-AU'); } catch { return iso; } };

export default function MyEngagementLetters() {
  const navigate = useNavigate();
  const [letters, setLetters] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const me = await api.get('/api/me');
        const cid = me.data.active_membership?.client_id;
        if (!cid) { setLoading(false); return; }
        const res = await API.listForClient(cid);
        setLetters(res.data.letters || []);
      } catch (err) {
        toast.error('Failed to load engagement letters');
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="p-6"><Loader2 className="w-6 h-6 animate-spin text-foreground" /></div>;

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }} data-testid="my-letters-title">My Engagement Letters</h1>
        <p className="text-muted-foreground mt-1">Your engagement letters and their signing status. Signed copies remain available here at any time.</p>
      </div>
      <Card>
        <CardContent className="pt-6">
          {letters.length === 0 ? (
            <div className="text-center py-12" data-testid="my-letters-empty">
              <FileSignature className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
              <p className="text-muted-foreground">No engagement letters yet.</p>
            </div>
          ) : (
            <Table>
              <TableHeader><TableRow className="bg-muted/50">
                <TableHead>Reference</TableHead><TableHead>Total</TableHead><TableHead>Sent</TableHead><TableHead>Signed</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {letters.map((l) => (
                  <TableRow key={l.id} data-testid={`my-letter-${l.id}`}>
                    <TableCell className="font-medium">{l.engagement_letter_number}</TableCell>
                    <TableCell className="text-sm">{fmtMoney(l.fee_summary?.total)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{fmt(l.sent_at)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{fmt(l.signed_at)}</TableCell>
                    <TableCell><Badge className={(LETTER_STATUS_META[l.status] || {}).color}>{(LETTER_STATUS_META[l.status] || {}).label || l.status}</Badge></TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="outline" data-testid={`my-letter-download-${l.id}`}
                          onClick={() => downloadLetterPdf(l.id, l.engagement_letter_number).catch(() => toast.error('Failed to download PDF'))}>
                          <Download className="w-4 h-4 mr-1" /> PDF
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => navigate('/engagement')}><Eye className="w-4 h-4 mr-1" /> View</Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
